import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Product } from '../types';
import { getProduct } from '../services/api';
import { getProductBySlug, DEMO_PRODUCTS } from '../data/products';
import { useCart } from '../context/CartContext';
import { HiOutlineHeart, HiOutlineShoppingBag, HiCheck } from 'react-icons/hi';
import { useCurrency } from '../context/CurrencyContext';

const ProductDetail: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const fallback = getProductBySlug(slug || '') || DEMO_PRODUCTS[0];
  const [product, setProduct] = useState<Product>(fallback);
  const [selectedSize, setSelectedSize] = useState('');
  const [selectedColor, setSelectedColor] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [added, setAdded] = useState(false);
  const [activeImage, setActiveImage] = useState(0);
  const { addItem } = useCart();
  const { formatPrice } = useCurrency();

  useEffect(() => {
    const found = getProductBySlug(slug || '');
    if (found) setProduct(found);

    if (slug) {
      getProduct(slug)
        .then(res => setProduct(res.data))
        .catch(() => {});
    }
  }, [slug]);

  const handleAddToCart = () => {
    addItem(product, quantity, selectedSize, selectedColor);
    setAdded(true);
    setTimeout(() => setAdded(false), 2000);
  };

  const hasDiscount = product.discount_price !== null;
  const displayPrice = hasDiscount ? product.discount_price : product.price;

  const images = [product.image, product.image_2, product.image_3].filter(Boolean);

  return (
    <div className="product-detail">
      <div className="breadcrumb">
        <Link to="/">Home</Link> / <Link to="/shop">Shop</Link> / <span>{product.name}</span>
      </div>

      <div className="product-detail-grid">
        <div className="product-images">
          <div className="product-main-image">
            {images.length > 0 ? (
              <img src={images[activeImage] || images[0]} alt={product.name} />
            ) : (
              <div className="image-placeholder">
                <span>YIBE</span>
              </div>
            )}
          </div>
          {images.length > 1 && (
            <div className="product-thumbnails">
              {images.map((img, i) => (
                <div
                  key={i}
                  className={`thumbnail ${activeImage === i ? 'active' : ''}`}
                  onClick={() => setActiveImage(i)}
                >
                  <img src={img} alt="" />
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="product-info">
          <p className="product-category-label">{product.category_name}</p>
          <h1 className="product-title">{product.name}</h1>

          <div className="product-price-detail">
            <span className="price-main">{formatPrice(displayPrice!)}</span>
            {hasDiscount && <span className="price-original">{formatPrice(product.price)}</span>}
            {hasDiscount && (
              <span className="discount-badge">
                {Math.round((1 - parseFloat(product.discount_price!) / parseFloat(product.price)) * 100)}% OFF
              </span>
            )}
          </div>

          <p className="product-description">{product.description}</p>

          {product.colors.length > 0 && (
            <div className="product-option">
              <label>Color {selectedColor && <span className="selected-label">- {selectedColor}</span>}</label>
              <div className="option-buttons">
                {product.colors.map(color => (
                  <button
                    key={color}
                    className={`option-btn ${selectedColor === color ? 'selected' : ''}`}
                    onClick={() => setSelectedColor(color)}
                  >
                    {color}
                  </button>
                ))}
              </div>
            </div>
          )}

          {product.sizes.length > 0 && (
            <div className="product-option">
              <label>Size {selectedSize && <span className="selected-label">- {selectedSize}</span>}</label>
              <div className="option-buttons">
                {product.sizes.map(size => (
                  <button
                    key={size}
                    className={`option-btn ${selectedSize === size ? 'selected' : ''}`}
                    onClick={() => setSelectedSize(size)}
                  >
                    {size}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="product-option">
            <label>Quantity</label>
            <div className="quantity-selector">
              <button onClick={() => setQuantity(Math.max(1, quantity - 1))}>-</button>
              <span>{quantity}</span>
              <button onClick={() => setQuantity(quantity + 1)}>+</button>
            </div>
          </div>

          <div className="product-actions">
            <button
              className={`btn btn-primary btn-lg add-to-cart-btn ${added ? 'added' : ''}`}
              onClick={handleAddToCart}
              disabled={!product.in_stock}
            >
              {added ? (
                <><HiCheck size={20} /> Added to Cart</>
              ) : (
                <><HiOutlineShoppingBag size={20} /> {product.in_stock ? 'Add to Cart' : 'Sold Out'}</>
              )}
            </button>
            <button className="btn btn-outline btn-icon">
              <HiOutlineHeart size={22} />
            </button>
          </div>

          <div className="product-meta">
            <p><strong>Category:</strong> {product.category_name}</p>
            <p><strong>Availability:</strong> {product.in_stock ? 'In Stock' : 'Out of Stock'}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetail;
